import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';

/**
 * Lazy Load service
 * @description Angular service that load asyncronous external javascript
 * with a cache system.
 */
@Injectable({
  providedIn: 'root'
})
export class LazyLoadService {
  private _loadedLibraries: { [url: string]: ReplaySubject<unknown> } = {};

  // eslint-disable-next-line @typescript-eslint/no-parameter-properties
  constructor(@Inject(DOCUMENT) private readonly document: Document) { }

  lazyLoadInline(text: string) {
    if (this._loadedLibraries[text]) {
      return this._loadedLibraries[text].asObservable();
    }

    this._loadedLibraries[text] = new ReplaySubject();

    const script = this.document.createElement('script');
    script.type = 'text/javascript';

    try {
      script.appendChild(document.createTextNode(text));

    } catch (e) {
      script.text = text;
    }

    script.onload = () => {
      this._loadedLibraries[text].next();
      this._loadedLibraries[text].complete();
    };

    this.document.body.appendChild(script);

    return this._loadedLibraries[text].asObservable();
  }

  lazyLoadExternal(url: string) {
    if (this._loadedLibraries[url]) {
      return this._loadedLibraries[url].asObservable();
    }

    this._loadedLibraries[url] = new ReplaySubject();

    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = url;
    script.onload = () => {
      this._loadedLibraries[url].next();
      this._loadedLibraries[url].complete();
    };

    this.document.body.appendChild(script);

    return this._loadedLibraries[url].asObservable();
  }
}
